import { useState, useRef, useCallback } from "react";

// ─── Icons ────────────────────────────────────────────────────────────────────

const UploadIcon = ({ size = 48 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="4" />
    <path d="M12 16V8M8.5 11.5 12 8l3.5 3.5" />
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const PlayIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" fill="rgba(0,0,0,0.45)" stroke="none" />
    <polygon points="10,8 17,12 10,16" fill="white" stroke="none" />
  </svg>
);

// ─── FileAttachment ───────────────────────────────────────────────────────────

/**
 * FileAttachment
 *
 * Props:
 *   accept        – MIME types string (default "image/*,video/*")
 *   maxSizeMB     – max file size in MB (default 100)
 *   onChange      – callback(file | null) called when attachment changes
 *   className     – extra classes on the root wrapper
 */
export default function FileAttachment({
  accept = "image/*,video/*",
  maxSizeMB = 10,
  onChange,
  className = "",
}) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [fileType, setFileType] = useState(null); // "image" | "video"
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");

  const fileRef = useRef();

  // ── helpers ──────────────────────────────────────────────────────────────

  const isAccepted = useCallback(
    (f) => {
      const acceptedTypes = accept
        .split(",")
        .map((t) => t.trim().replace("*", ""));
      return acceptedTypes.some((t) => f.type.startsWith(t.replace("/", "")));
    },
    [accept]
  );

  const commit = useCallback(
    (f) => {
      setError("");

      if (!f.type.startsWith("image/") && !f.type.startsWith("video/")) {
        setError("Only image and video files are supported.");
        return;
      }

      if (f.size > maxSizeMB * 1024 * 1024) {
        setError(`File must be under ${maxSizeMB} MB.`);
        return;
      }

      const url = URL.createObjectURL(f);
      const type = f.type.startsWith("image/") ? "image" : "video";

      setFile(f);
      setPreview(url);
      setFileType(type);
      onChange?.(f);
    },
    [maxSizeMB, onChange]
  );

  const remove = () => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null);
    setPreview(null);
    setFileType(null);
    setError("");
    if (fileRef.current) fileRef.current.value = "";
    onChange?.(null);
  };

  // ── event handlers ────────────────────────────────────────────────────────

  const handleChange = (e) => {
    if (e.target.files[0]) commit(e.target.files[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files[0]) commit(e.dataTransfer.files[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className={`w-full ${className}`}>
      {preview ? (
        // ── Preview ──────────────────────────────────────────────────────
        <div className="relative rounded-2xl overflow-hidden border border-gray-200 mb-1">
          {fileType === "image" ? (
        <div className="flex justify-center items-center">
  <img
    src={preview}
    alt="attachment preview"
    className="object-cover"
  />
</div>
          ) : (
            <div className="relative w-full max-h-[220px] sm:max-h-[280px] bg-black flex items-center justify-center overflow-hidden">
              <video
                src={preview}
                controls
                className="w-full max-h-[220px] sm:max-h-[280px] object-contain"
                muted
                playsInline
                preload="metadata"
              />
              {/* <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <PlayIcon />
              </div> */}
            </div>
          )}

          {/* File name bar */}
          {/* <div className="absolute bottom-0 left-0 right-0 bg-black/40 backdrop-blur-sm px-3 py-1.5 flex items-center gap-2">
            <span className="text-white text-xs truncate flex-1 font-mono">
              {file?.name}
            </span>
            <span className="text-white/60 text-xs shrink-0">
              {(file?.size / (1024 * 1024)).toFixed(1)} MB
            </span>
          </div> */}

          {/* Remove button */}
          <button
            onClick={remove}
            className="absolute top-3 right-3 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center hover:bg-black/70 transition-colors"
            aria-label="Remove attachment"
          >
            <CloseIcon />
          </button>
        </div>
      ) : (
        // ── Drop Zone ─────────────────────────────────────────────────────
        <div
          onClick={() => fileRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && fileRef.current?.click()}
          className={`rounded-2xl border-2 border-dashed cursor-pointer transition-all flex flex-col items-center justify-center py-8 sm:py-10 mb-1 px-4 select-none ${
            dragOver
              ? "border-[#1F3A60] bg-[#1F3A60]/5"
              : "border-[#B5B5B5] hover:border-[#1F3A60]"
          }`}
        >
          <span
            className={`mb-3 transition-colors ${
              dragOver ? "text-[#1F3A60]" : "text-[#B5B5B5]"
            }`}
          >
            <UploadIcon size={typeof window !== "undefined" && window.innerWidth < 640 ? 46 : 64} />
          </span>

          <p className="text-[14px] sm:text-[15px] text-[#000000] font-body text-center leading-relaxed">
            Drop your image or video here, or{" "}
            <span className="font-bold text-[#1F3A60] underline">browse</span>
          </p>
          <p className="text-xs text-[#B5B5B5] mt-1">
            One file at a time · Max {maxSizeMB} MB
          </p>
        </div>
      )}

      {/* Error */}
      {error && (
        <p className="text-red-500 text-xs mt-1 px-1">{error}</p>
      )}

      {/* Hidden input */}
      <input
        type="file"
        ref={fileRef}
        accept={accept}
        onChange={handleChange}
        className="hidden"
      />
    </div>
  );
}